import { Analytics } from "@vercel/analytics/react";
import { Suspense, lazy, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import Navbar from "@/components/Navbar";
import BottomNav from "@/components/BottomNav";
import SuspendedBanner from "@/components/SuspendedBanner";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import { SEOHelmet } from "@/components/SEOHelmet";
import CookieConsent from "@/components/CookieConsent";
import PrivacyPolicyConsentDialog from "@/components/PrivacyPolicyConsentDialog";
import PullToRefresh from "@/components/PullToRefresh";
import InstallPromptBanner from "@/components/InstallPromptBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import AppSplash from "@/components/AppSplash";
import { useCookieConsent } from "@/hooks/use-cookie-consent";
import Index from "./pages/Index";
import { Loader2 } from "lucide-react";

// Strona główna ładowana od razu (LCP), reszta dopiero przy wejściu na daną podstronę.
const Auth = lazy(() => import("./pages/Auth"));
const Collection = lazy(() => import("./pages/Collection"));
const AddKit = lazy(() => import("./pages/AddKit"));
const KitDetail = lazy(() => import("./pages/KitDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const Explore = lazy(() => import("./pages/Explore"));
const ForSale = lazy(() => import("./pages/ForSale"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Collectors = lazy(() => import("./pages/Collectors"));
const Regulamin = lazy(() => import("./pages/Regulamin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NewPassword = lazy(() => import("./pages/NewPassword"));
const PolitykaPrywatnosci = lazy(() => import("./pages/PolitykaPrywatnosci"));
const Pogadajmy = lazy(() => import("./pages/Pogadajmy"));
const Admin = lazy(() => import("./pages/Admin"));
const Messages = lazy(() => import("./pages/Messages"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      gcTime: 5 * 60_000,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex flex-1 items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

const SentryRoutes = Routes;

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { passwordRecoveryRequired } = useAuth();
  const { analyticsEnabled, ready: consentReady } = useCookieConsent();
  const isMessagesPage = location.pathname === "/wiadomosci";

  useEffect(() => {
    if (!passwordRecoveryRequired) return;
    if (location.pathname === "/reset-password" || location.pathname === "/reset-password/new") return;

    navigate("/reset-password/new", { replace: true });
  }, [location.pathname, navigate, passwordRecoveryRequired]);

  // Lock the page itself on Messages so only the inner panels scroll.
  useEffect(() => {
    if (!isMessagesPage) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      window.scrollTo(0, 0);
    };
  }, [isMessagesPage]);

  return (
    <div className={isMessagesPage ? "flex h-[100dvh] flex-col overflow-hidden" : "flex min-h-screen flex-col"}>
      <SEOHelmet />
      {!passwordRecoveryRequired && (
        <>
          <Navbar />
          <div className="h-16 shrink-0 md:h-20" aria-hidden="true" />
          <SuspendedBanner />
        </>
      )}
      <main className={isMessagesPage ? "min-h-0 flex-1 overflow-hidden" : "flex-1"}>
        <Suspense fallback={<PageLoader />}>
          <ErrorBoundary>
          <SentryRoutes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/collection" element={<Collection />} />
            <Route path="/collection/:userId" element={<Collection />} />
            <Route path="/add-kit" element={<AddKit />} />
            <Route path="/kit/:id" element={<KitDetail />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/na-sprzedaz" element={<ForSale />} />
            <Route path="/collectors" element={<Collectors />} />
            <Route path="/wiadomosci" element={<Messages />} />
            <Route path="/messages" element={<Navigate to="/wiadomosci" replace />} />
            <Route path="/pogadajmy" element={<Pogadajmy />} />
            <Route path="/pogadajmy/:postId" element={<Pogadajmy />} />
            <Route path="/regulamin" element={<Regulamin />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password/new" element={<NewPassword />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosci />} />
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
          </ErrorBoundary>
        </Suspense>
      </main>
      {!isMessagesPage && <Footer />}
      {/* Odstęp na dolny pasek nawigacji (mobile) */}
      <div className="h-[72px] shrink-0 md:hidden" aria-hidden="true" />
      <BottomNav />
      <InstallPromptBanner />
      <PullToRefresh />
      <CookieConsent />
      <PrivacyPolicyConsentDialog />
      <AppSplash />
      {consentReady && analyticsEnabled && <Analytics />}
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppLayout />
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
